<template>
  <div class="page-container">
    <PageHeader title="系统通知">
      <template #actions>
        <el-button :disabled="!unreadCount" size="small" @click="handleReadAll">全部已读</el-button>
        <el-button type="danger" plain size="small" :disabled="!hasSelection" @click="handleDeleteSelected">批量删除</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-input v-model.trim="query.noticeTitle" placeholder="按标题筛选" size="small" clearable @keyup.enter.native="handleSearch"></el-input>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :total="total"
        row-id-key="noticeId"
        stripe
        use-internal-pagination
        @selection-change="handleSelectionChange"
        @page-change="handlePageChange"
      >
        <template #cell-noticeStatus="{ row }">
          <el-tag :type="row.noticeStatus === 2 ? 'info' : 'danger'" size="mini">{{ row.noticeStatus === 2 ? '已读' : '未读' }}</el-tag>
        </template>
        <template #cell-operation="{ row }">
          <div class="notice-actions">
            <el-button type="text" size="small" :disabled="row.noticeStatus === 2" @click="handleReadOne(row)">已读</el-button>
            <el-button type="text" size="small" class="text-danger" @click="handleDeleteOne(row)">删除</el-button>
          </div>
        </template>
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无通知" icon="el-icon-bell"></EmptyState>
    </TablePanel>
  </div>
</template>

<script>
import { deleteSysNoticeRead, getSysNoticeList, updateSysNoticeRead, updateSysNoticeReadAll } from '@/api/notice'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

const NOTICE_COLUMNS = [
  { type: 'selection', width: 44 },
  { prop: 'noticeTitle', label: '标题', minWidth: 160 },
  { prop: 'noticeContent', label: '内容', minWidth: 220 },
  { prop: 'createtime', label: '发布时间', width: 150 },
  { prop: 'noticeStatus', label: '状态', width: 80 },
  { type: 'operation', prop: 'operation', label: '操作', width: 140, fixed: 'right' }
]

export default {
  name: 'SystemNoticePage',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      actionLoading: false,
      list: [],
      total: 0,
      selectedRows: [],
      columns: NOTICE_COLUMNS,
      query: { noticeTitle: '', currentPage: 1, pageSize: 10 }
    }
  },
  computed: {
    hasSelection () { return this.selectedRows.length > 0 },
    unreadCount () { return this.list.filter(item => item.noticeStatus === 1).length },
    selectedIds () { return this.selectedRows.map(item => item.noticeId).filter(Boolean) }
  },
  created () { this.fetchList() },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const res = await getSysNoticeList(this.query)
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSelectionChange (rows) {
      this.selectedRows = rows
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { noticeTitle: '', currentPage: 1, pageSize: 10 }
      this.selectedRows = []
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },
    async runNoticeAction (handler, successMessage) {
      if (this.actionLoading) return
      this.actionLoading = true
      try {
        await handler()
        this.$message.success(successMessage)
        this.selectedRows = []
        await this.fetchList()
        this.$root.$emit('notice:refreshUnread')
      } finally {
        this.actionLoading = false
      }
    },
    async handleReadOne (row) {
      await this.runNoticeAction(() => updateSysNoticeRead([row.noticeId]), '已标记为已读')
    },
    async handleReadAll () {
      await this.runNoticeAction(() => updateSysNoticeReadAll(), '全部已标记为已读')
    },
    async handleDeleteOne (row) {
      await this.$confirm('确认删除这条通知吗？', '提示', { type: 'warning' })
      await this.runNoticeAction(() => deleteSysNoticeRead([row.noticeId]), '通知已删除')
    },
    async handleDeleteSelected () {
      if (!this.selectedIds.length) return
      await this.$confirm('确认删除选中的通知吗？', '提示', { type: 'warning' })
      await this.runNoticeAction(() => deleteSysNoticeRead(this.selectedIds), '选中通知已删除')
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.notice-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.text-danger { color: #ef4444 !important; }
</style>
